import { useState, useMemo } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Calendar,
  Building2,
  DollarSign,
  Clock,
  MapPin,
  Users,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Phone,
  Play,
  Ban,
  Package,
  Edit,
  Eye,
  Info
} from "lucide-react";
import { useUpdateBookingStatus } from "@/hooks/useBookings";
import type { BookingWithOptionsResponse, BookingStatus, BookingOptionResponse } from "@/types/activities";
import { getBookingStatusColor, getBookingOptionStatusColor, BookingOptionStatus } from "@/types/activities";
import FinalBookingConfirmation from "./FinalBookingConfirmation";
import CancelBookingModal from "./CancelBookingModal";
import BookingWorkflowProgress from "./BookingWorkflowProgress";
import UpdateBookingOptionModal from "./UpdateBookingOptionModal";
import BookingOptionsList from "./BookingOptionsList";
import { useToast } from "@/hooks/use-toast";

const getStatusIcon = (status: BookingStatus) => {
  switch (status) {
    case "Confirmed": return <CheckCircle className="w-4 h-4 text-green-500" />;
    case "Cancelled": return <XCircle className="w-4 h-4 text-red-500" />;
    case "NoVendorsAvailable": return <Ban className="w-4 h-4 text-red-500" />;
    case "PendingVendorContact": return <Clock className="w-4 h-4 text-blue-500" />;
    case "VendorNegotiating": return <Phone className="w-4 h-4 text-yellow-500" />;
    case "PendingOrganizationApproval": return <Eye className="w-4 h-4 text-purple-500" />;
    case "ApprovedAwaitingVendorBooking": return <Calendar className="w-4 h-4 text-orange-500" />;
    default: return <AlertCircle className="w-4 h-4 text-gray-500" />;
  }
};

const getOptionStatusIcon = (status: BookingOptionStatus) => {
  switch (status) {
    case BookingOptionStatus.Exploring: return '🔍';
    case BookingOptionStatus.Available: return '✅';
    case BookingOptionStatus.Unavailable: return '❌';
    case BookingOptionStatus.Rejected: return '👎';
    case BookingOptionStatus.Approved: return '👍';
    default: return '⏳';
  }
};

interface BookingDetailDrawerProps {
  booking: BookingWithOptionsResponse | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRefresh?: () => void;
}

export default function BookingDetailDrawer({
  booking,
  open,
  onOpenChange,
  onRefresh
}: BookingDetailDrawerProps) {
  const { toast } = useToast();

  const [showUpdateOptionModal, setShowUpdateOptionModal] = useState(false);
  const [showFinalConfirmationModal, setShowFinalConfirmationModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState<BookingOptionResponse | null>(null);

  const updateBookingStatus = useUpdateBookingStatus();

  if (!booking) return null;

  // Get the active booking option (the one currently being worked on)
  const activeOption = useMemo(() => {
    if (!booking?.bookingOptions || booking.bookingOptions.length === 0) return null;

    // Find the first option that's not Rejected or Unavailable
    return booking.bookingOptions.find(
      opt => opt.status !== BookingOptionStatus.Rejected &&
             opt.status !== BookingOptionStatus.Unavailable
    ) || null;
  }, [booking?.bookingOptions]);

  // Get rejected/unavailable options for history
  const previousOptions = useMemo(() => {
    if (!booking?.bookingOptions) return [];
    return booking.bookingOptions.filter(
      opt => opt.status === BookingOptionStatus.Rejected ||
             opt.status === BookingOptionStatus.Unavailable
    ).sort((a, b) => b.optionOrder - a.optionOrder);
  }, [booking?.bookingOptions]);

  // Can add new option only if all existing options are rejected/unavailable
  const canAddNewOption = useMemo(() => {
    if (!booking?.bookingOptions || booking.bookingOptions.length === 0) return false;
    return booking.bookingOptions.every(
      opt => opt.status === BookingOptionStatus.Rejected ||
             opt.status === BookingOptionStatus.Unavailable
    );
  }, [booking?.bookingOptions]);

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Not specified";
    return new Date(dateString).toLocaleDateString();
  };

  const formatPrice = (price?: number) => {
    if (!price) return "Pending";
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(price);
  };

  // Handle "Start Working" button - changes status from PendingVendorContact to VendorNegotiating
  const handleStartWorking = async () => {
    try {
      await updateBookingStatus.mutateAsync({
        bookingId: booking.id,
        data: {
          status: "VendorNegotiating" as BookingStatus,
          notes: "Athlyn team started working on this booking"
        }
      });

      toast({
        title: "Status updated",
        description: "You're now working on this booking. Update Option #1 with vendor response."
      });

      onRefresh?.();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update booking status",
        variant: "destructive"
      });
    }
  };

  const handleEditOption = (option: BookingOptionResponse) => {
    setSelectedOption(option);
    setShowUpdateOptionModal(true);
  };

  const handleConfirmBooking = () => {
    if (activeOption && activeOption.status === BookingOptionStatus.Approved) {
      setSelectedOption(activeOption);
      setShowFinalConfirmationModal(true);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-4xl overflow-hidden flex flex-col">
          <SheetHeader className="pb-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Package className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <SheetTitle className="text-lg">
                    {booking.activityName || "Booking Request"}
                  </SheetTitle>
                  <SheetDescription className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    {booking.organizationName}
                    <span className="text-muted-foreground">•</span>
                    {booking.estimatedParticipantsCount} participants
                  </SheetDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getStatusIcon(booking.status)}
                <Badge
                  variant="outline"
                  className={`${getBookingStatusColor(booking.status)}`}
                >
                  {booking.status.replace(/([A-Z])/g, ' $1').trim()}
                </Badge>
              </div>
            </div>
          </SheetHeader>

          <ScrollArea className="flex-1 -mx-6 px-6">
            <div className="space-y-6 pb-6">
              {/* Workflow Progress */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Workflow Progress
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <BookingWorkflowProgress
                    currentStatus={booking.status}
                    orientation="horizontal"
                    showDescriptions={false}
                  />
                </CardContent>
              </Card>

              {/* Primary Action - Start Working Button */}
              {booking.status === "PendingVendorContact" && (
                <Alert className="bg-blue-50 border-blue-200">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    <div className="flex items-center justify-between">
                      <div>
                        <strong className="block mb-1">Next Step: Start Working</strong>
                        <p className="text-sm">
                          Change status to "Vendor Negotiating" and begin working on Option #1.
                        </p>
                      </div>
                      <Button
                        onClick={handleStartWorking}
                        disabled={updateBookingStatus.isPending}
                        size="sm"
                        className="ml-4 flex items-center gap-2"
                      >
                        <Play className="w-3 h-3" />
                        {updateBookingStatus.isPending ? "Starting..." : "Start Working"}
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Active Booking Option */}
              {activeOption && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Current Option (#{activeOption.optionOrder})
                      </CardTitle>
                      <Badge variant="outline" className={getBookingOptionStatusColor(activeOption.status)}>
                        {getOptionStatusIcon(activeOption.status)} {activeOption.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Auto-created notice for Option #1 */}
                    {activeOption.optionOrder === 1 && (
                      <Alert className="bg-blue-50 border-blue-200">
                        <Info className="h-4 w-4 text-blue-600" />
                        <AlertDescription className="text-blue-800 text-xs">
                          <strong>🤖 Auto-Created:</strong> This option was automatically created when the organization submitted their booking request.
                        </AlertDescription>
                      </Alert>
                    )}

                    {/* Option Details */}
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <p className="font-medium text-muted-foreground">Service</p>
                        <p>{activeOption.serviceName || 'Service'}</p>
                      </div>
                      <div>
                        <p className="font-medium text-muted-foreground">Vendor</p>
                        <p>{activeOption.vendorName || 'Unknown'}</p>
                      </div>
                    </div>

                    {/* Available Option Details */}
                    {activeOption.status === BookingOptionStatus.Available && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg space-y-2">
                        <h4 className="text-xs font-medium text-green-800">✅ Vendor Available</h4>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="flex items-center gap-1">
                            <DollarSign className="h-3 w-3 text-green-700" />
                            <span className="font-semibold text-green-900">{formatPrice(activeOption.proposedPrice)}</span>
                          </div>
                          {activeOption.proposedCapacity && (
                            <div className="flex items-center gap-1">
                              <Users className="h-3 w-3 text-green-700" />
                              <span className="text-green-900">{activeOption.proposedCapacity} people</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      {/* Only show update button when status is Exploring (vendor response not yet submitted) */}
                      {activeOption.status === BookingOptionStatus.Exploring && (
                        <Button
                          onClick={() => handleEditOption(activeOption)}
                          size="sm"
                          className="flex items-center gap-2"
                        >
                          <Edit className="w-3 h-3" />
                          Submit Vendor Response
                        </Button>
                      )}

                      {/* Show confirm booking button when approved by organization */}
                      {booking.status === "ApprovedAwaitingVendorBooking" && activeOption.status === BookingOptionStatus.Approved && (
                        <Button
                          onClick={handleConfirmBooking}
                          size="sm"
                          className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-3 h-3" />
                          Confirm Booking
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Booking Options List */}
              <BookingOptionsList
                bookingId={booking.id}
                bookingStatus={booking.status}
                canAddOptions={true}
              />

              {/* Booking Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Booking Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="font-medium text-muted-foreground">Booking ID</p>
                      <p className="font-mono">{booking.id.slice(0, 8)}...</p>
                    </div>
                    <div>
                      <p className="font-medium text-muted-foreground">Created</p>
                      <p className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDateTime(booking.createdAt)}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-muted-foreground">Participants</p>
                      <p className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {booking.estimatedParticipantsCount}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-muted-foreground">Preferred Date</p>
                      <p className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(booking.preferredStartDate)}
                      </p>
                    </div>
                  </div>

                  {booking.specialRequirements && (
                    <>
                      <Separator />
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-2">Special Requirements</p>
                        <p className="text-xs bg-muted/30 p-2 rounded-md">{booking.specialRequirements}</p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Previous Options History */}
              {previousOptions.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Previous Options ({previousOptions.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {previousOptions.map((option) => (
                      <div key={option.id} className="p-2 bg-muted/30 rounded-lg border text-xs">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span>{getOptionStatusIcon(option.status)}</span>
                            <div>
                              <p className="font-medium">Option #{option.optionOrder}: {option.serviceOptionName}</p>
                              <p className="text-muted-foreground">{option.vendorName}</p>
                            </div>
                          </div>
                          <Badge variant="outline" className={getBookingOptionStatusColor(option.status)}>
                            {option.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          </ScrollArea>

          <div className="pt-4 border-t flex gap-2">
            <Button
              onClick={() => setShowCancelModal(true)}
              variant="outline"
              className="text-red-600 hover:text-red-700"
            >
              Cancel Booking
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Modals */}
      {selectedOption && (
        <UpdateBookingOptionModal
          open={showUpdateOptionModal}
          onClose={() => {
            setShowUpdateOptionModal(false);
            setSelectedOption(null);
          }}
          bookingId={booking.id}
          option={selectedOption}
          onSuccess={() => {
            onRefresh?.();
            setShowUpdateOptionModal(false);
            setSelectedOption(null);
          }}
        />
      )}

      {selectedOption && (
        <FinalBookingConfirmation
          open={showFinalConfirmationModal}
          onClose={() => {
            setShowFinalConfirmationModal(false);
            setSelectedOption(null);
          }}
          booking={booking}
          selectedOption={selectedOption}
          onConfirm={async (confirmationData) => {
            console.log('Confirming booking with data:', confirmationData);
            // TODO: Implement confirmation API call
            setShowFinalConfirmationModal(false);
            setSelectedOption(null);
          }}
        />
      )}

      <CancelBookingModal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        booking={booking}
        onSuccess={() => {
          setShowCancelModal(false);
          onOpenChange(false);
          onRefresh?.();
        }}
      />
    </>
  );
}
